import {
  fetchPersonnelPeople,
  fetchPersonnelAbsences,
  fetchPersonnelPublicHolidays,
  fetchPersonnelHolidayWork,
  fetchPersonnelWorkDays,
} from "../api/personnelApi";

import {
  loadPersonnelResource,
} from "../utils/personnelCache";

const currentPrefetches =
  new Map();

/* =========================================================
 * YEAR RANGES
 * ======================================================= */

export const getPersonnelYearRanges =
  (
    year =
      new Date()
        .getFullYear()
  ) => {
    const numericYear =
      Number(year);

    return {
      absences: {
        start:
          `${numericYear}-01-01T00:00:00.000Z`,

        end:
          `${
            numericYear + 1
          }-01-01T00:00:00.000Z`,
      },

      publicHolidays: {
        start:
          `${numericYear}-01-01`,

        end:
          `${numericYear}-12-31`,
      },

      holidayWork: {
        start:
          `${numericYear}-01-01T00:00:00.000Z`,

        end:
          `${
            numericYear + 1
          }-01-01T00:00:00.000Z`,
      },

      workDays: {
        start:
          `${numericYear}-01-01`,

        end:
          `${numericYear}-12-31`,
      },
    };
  };

/* =========================================================
 * PREFETCH SCOPE
 * ======================================================= */

export const getPersonnelPrefetchScopeFromPath =
  (
    pathname
  ) => {
    const path =
      String(
        pathname ||
          ""
      );

    if (
      path.startsWith(
        "/personnel/requests"
      )
    ) {
      return "requests";
    }

    if (
      path.startsWith(
        "/personnel/public-holidays"
      )
    ) {
      return "publicHolidays";
    }

    if (
      path.startsWith(
        "/personnel/compensation"
      )
    ) {
      return "compensation";
    }

    return "calendar";
  };

/* =========================================================
 * FOUNDATION PREFETCH
 * ======================================================= */

export const prefetchPersonnelFoundation =
  async ({
    year =
      new Date()
        .getFullYear(),

    scope =
      "calendar",

    force =
      false,
  } = {}) => {
    const ranges =
      getPersonnelYearRanges(
        year
      );

    const normalizedScope =
      [
        "calendar",
        "requests",
        "publicHolidays",
        "compensation",
      ].includes(
        scope
      )
        ? scope
        : "calendar";

    const prefetchKey =
      `${normalizedScope}:${year}`;

    if (
      currentPrefetches.has(
        prefetchKey
      ) &&
      !force
    ) {
      return currentPrefetches.get(
        prefetchKey
      );
    }

    const tasks =
      [];

    if (
      normalizedScope ===
      "calendar"
    ) {
      tasks.push(
        loadPersonnelResource({
          resource:
            "people",

          params: {},

          force,

          loader:
            () =>
              fetchPersonnelPeople(),
        }),

        loadPersonnelResource({
          resource:
            "absences",

          params:
            ranges.absences,

          force,

          loader:
            () =>
              fetchPersonnelAbsences(
                ranges.absences
              ),
        }),

        loadPersonnelResource({
          resource:
            "publicHolidays",

          params:
            ranges.publicHolidays,

          force,

          loader:
            () =>
              fetchPersonnelPublicHolidays(
                ranges.publicHolidays
              ),
        }),

        loadPersonnelResource({
          resource:
            "holidayWork",

          params:
            ranges.holidayWork,

          force,

          loader:
            () =>
              fetchPersonnelHolidayWork(
                ranges.holidayWork
              ),
        }),

        loadPersonnelResource({
          resource:
            "workDays",

          params:
            ranges.workDays,

          force,

          loader:
            () =>
              fetchPersonnelWorkDays(
                ranges.workDays
              ),
        })
      );
    }

    if (
      normalizedScope ===
      "requests"
    ) {
      tasks.push(
        loadPersonnelResource({
          resource:
            "people",

          params: {},

          force,

          loader:
            () =>
              fetchPersonnelPeople(),
        }),

        loadPersonnelResource({
          resource:
            "absenceRequests",

          params: {},

          force,

          loader:
            () =>
              fetchPersonnelAbsences(),
        })
      );
    }

    if (
      normalizedScope ===
      "publicHolidays"
    ) {
      tasks.push(
        loadPersonnelResource({
          resource:
            "publicHolidays",

          params:
            ranges.publicHolidays,

          force,

          loader:
            () =>
              fetchPersonnelPublicHolidays(
                ranges.publicHolidays
              ),
        })
      );
    }

    if (
      normalizedScope ===
      "compensation"
    ) {
      tasks.push(
        loadPersonnelResource({
          resource:
            "people",

          params: {},

          force,

          loader:
            () =>
              fetchPersonnelPeople(),
        }),

        loadPersonnelResource({
          resource:
            "publicHolidays",

          params:
            ranges.publicHolidays,

          force,

          loader:
            () =>
              fetchPersonnelPublicHolidays(
                ranges.publicHolidays
              ),
        }),

        loadPersonnelResource({
          resource:
            "holidayWork",

          params:
            ranges.holidayWork,

          force,

          loader:
            () =>
              fetchPersonnelHolidayWork(
                ranges.holidayWork
              ),
        })
      );
    }

    const prefetch =
      Promise.allSettled(
        tasks
      )
        .finally(
          () => {
            currentPrefetches.delete(
              prefetchKey
            );
          }
        );

    currentPrefetches.set(
      prefetchKey,
      prefetch
    );

    return prefetch;
  };